import React, { useCallback, useEffect, useState } from 'react'
import {
	Button,
	Card,
	Form,
	Input,
	Modal,
	Popconfirm,
	Space,
	Typography,
	message,
} from 'antd'
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import GlobalHeader from '../Header/GlobalHeader'
import PageHeader from '../Header/PageHeader'
import ReactBeatLoader from '../Spinner/ReactBeatLoader'
import { AntTable } from '../Table/AntTable'

const { Text } = Typography

export interface ReferenceDataItem {
	id: string
	name: string
}

export interface ReferenceDataService {
	fetchListing: () => Promise<any>
	create: (params: { name: string; id?: string }) => Promise<any>
	update: (id: string, params: { name?: string }) => Promise<any>
	delete: (id: string) => Promise<any>
}

interface ReferenceDataCrudPageProps {
	title: string
	singularLabel: string
	idFieldLabel: string
	nameFieldLabel: string
	icon?: React.ReactNode
	service: ReferenceDataService
	/** When true, renders only the table/card (for use inside a parent page with shared chrome). */
	embedded?: boolean
}

const toReferenceId = (name: string) => name.trim().replace(/ /g, '_')

/** Must stay aligned with backend REFERENCE_ID_PATTERN */
const REFERENCE_ID_PATTERN = /^[A-Za-z0-9_\u1780-\u17FF()\-]+$/

function ReferenceDataCrudPage({
	title,
	singularLabel,
	idFieldLabel,
	nameFieldLabel,
	icon,
	service,
	embedded = false,
}: ReferenceDataCrudPageProps) {
	const [form] = Form.useForm()
	const [spinner, setSpinner] = useState(false)
	const [items, setItems] = useState<ReferenceDataItem[]>([])
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [editingItem, setEditingItem] = useState<ReferenceDataItem | null>(null)
	const [saving, setSaving] = useState(false)

	const fetchData = useCallback(async () => {
		setSpinner(true)
		try {
			const res: any = await service.fetchListing()
			if (res?.statusCode === 200) {
				setItems(res?.data || [])
			}
		} catch (error: any) {
			message.error(error?.message || `Failed to load ${title.toLowerCase()}`)
		} finally {
			setSpinner(false)
		}
	}, [service, title])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	const handleCreate = () => {
		setEditingItem(null)
		form.resetFields()
		setIsModalVisible(true)
	}

	const handleEdit = (item: ReferenceDataItem) => {
		setEditingItem(item)
		form.setFieldsValue({ id: item.id, name: item.name })
		setIsModalVisible(true)
	}

	const handleDelete = async (id: string) => {
		try {
			const res: any = await service.delete(id)
			if (res?.statusCode === 200) {
				message.success(`${singularLabel} deleted successfully`)
				await fetchData()
			}
		} catch (error: any) {
			message.error(error?.message || `Failed to delete ${singularLabel.toLowerCase()}`)
		}
	}

	const handleSubmit = async () => {
		try {
			const values = await form.validateFields()
			setSaving(true)

			if (editingItem) {
				const res: any = await service.update(editingItem.id, { name: values.name })
				if (res?.statusCode === 200) {
					message.success(`${singularLabel} updated successfully`)
				}
			} else {
				const payload: { name: string; id?: string } = { name: values.name }
				if (values.id?.trim()) {
					payload.id = values.id.trim()
				}
				const res: any = await service.create(payload)
				if (res?.statusCode === 200) {
					message.success(`${singularLabel} created successfully`)
				}
			}

			setIsModalVisible(false)
			form.resetFields()
			await fetchData()
		} catch (error: any) {
			if (error?.errorFields) return
			message.error(error?.message || `Failed to save ${singularLabel.toLowerCase()}`)
		} finally {
			setSaving(false)
		}
	}

	const columns: ColumnsType<ReferenceDataItem> = [
		{
			title: idFieldLabel,
			dataIndex: 'id',
			key: 'id',
			align: 'center',
		},
		{
			title: nameFieldLabel,
			dataIndex: 'name',
			key: 'name',
			align: 'center',
		},
		{
			title: 'Action',
			key: 'action',
			align: 'center',
			render: (_: unknown, record: ReferenceDataItem) => (
				<Space size='small'>
					<Button type='link' icon={<EditOutlined />} onClick={() => handleEdit(record)}>
						Edit
					</Button>
					<Popconfirm
						title={`Delete this ${singularLabel.toLowerCase()}?`}
						description='Cannot delete if assigned to staff or admin users.'
						onConfirm={() => handleDelete(record.id)}
						okText='Delete'
						cancelText='Cancel'
						okButtonProps={{ danger: true }}
					>
						<Button type='link' danger icon={<DeleteOutlined />}>
							Delete
						</Button>
					</Popconfirm>
				</Space>
			),
		},
	]

	const tableCard = (
		<Card
			style={{ borderRadius: 8, boxShadow: embedded ? undefined : '0 1px 3px rgba(0,0,0,0.06)' }}
			styles={{ body: { padding: 0 } }}
			extra={
				embedded ? (
					<Button type='primary' icon={<PlusOutlined />} onClick={handleCreate}>
						Add {singularLabel}
					</Button>
				) : undefined
			}
		>
			{spinner ? (
				<div style={{ padding: 24 }}>
					<ReactBeatLoader />
				</div>
			) : (
				<AntTable
					columns={columns}
					dataSource={items}
					rowKey='id'
					locale={{
						emptyText: (
							<div style={{ padding: '32px 0' }}>
								<Text type='secondary'>
									No {title.toLowerCase()} yet. Click &quot;Add {singularLabel}&quot; to create one.
								</Text>
							</div>
						),
					}}
				/>
			)}
		</Card>
	)

	const modal = (
		<Modal
			title={editingItem ? `Edit ${singularLabel}` : `Add ${singularLabel}`}
			open={isModalVisible}
			onOk={handleSubmit}
			onCancel={() => {
				setIsModalVisible(false)
				form.resetFields()
			}}
			okText={editingItem ? 'Update' : 'Create'}
			confirmLoading={saving}
			destroyOnClose
		>
			<Form
				form={form}
				layout='vertical'
				onValuesChange={(changed) => {
					if (!editingItem && changed.name && !form.getFieldValue('id')) {
						form.setFieldValue('id', toReferenceId(changed.name))
					}
				}}
			>
				<Form.Item
					name='name'
					label={nameFieldLabel}
					rules={[{ required: true, message: `Please enter ${nameFieldLabel.toLowerCase()}` }]}
				>
					<Input placeholder={`Enter ${nameFieldLabel.toLowerCase()}`} />
				</Form.Item>
					<Form.Item
						name='id'
						label={idFieldLabel}
						rules={[
							{ required: true, message: `Please enter ${idFieldLabel.toLowerCase()}` },
							{
								pattern: REFERENCE_ID_PATTERN,
								message:
									'Use only letters, numbers, underscores, hyphens, parentheses, or Khmer characters',
							},
						]}
					extra={
						editingItem
							? 'ID cannot be changed after creation.'
							: 'Auto-generated from name. Spaces become underscores.'
					}
				>
					<Input placeholder={`Enter ${idFieldLabel.toLowerCase()}`} disabled={!!editingItem} />
				</Form.Item>
			</Form>
		</Modal>
	)

	if (embedded) {
		return (
			<>
				{tableCard}
				{modal}
			</>
		)
	}

	return (
		<>
			<GlobalHeader />

			<PageHeader
				title={title}
				subtitle={`${items.length} ${title.toLowerCase().slice(0, -1)}${items.length !== 1 ? 's' : ''}`}
				icon={icon}
				extra={
					<Button type='primary' icon={<PlusOutlined />} onClick={handleCreate}>
						Add {singularLabel}
					</Button>
				}
			/>

			<div style={{ padding: '12px 24px 24px' }}>{tableCard}</div>

			{modal}
		</>
	)
}

export default ReferenceDataCrudPage
